import React, { useRef, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
} from "framer-motion";
import { Container, Row, Col } from "react-bootstrap";
import { IoClose } from "react-icons/io5";

const ContactStack = ({ onClose }) => {
  const containerRef = useRef(null);
  const formRef = useRef(null);

  /* ---------------- FORM SUBMIT HANDLER ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(formRef.current);

    // Optional identifier
    formData.append("form_type", "Ascent Contact Drawer Form");

    // Extract fields for CRM integration
    const nameVal = formData.get("name") || "";
    const emailVal = formData.get("email") || "";
    const companyVal = formData.get("company") || "";
    const phoneVal = formData.get("phone") || "Not Provided";
    const messageVal = formData.get("message") || "";

    // Geolocation detection by IP address lookup
    let countryVal = "Malaysia";
    let stateVal = "";
    try {
      const geoRes = await fetch("https://ipapi.co/json/");
      if (geoRes.ok) {
        const geoData = await geoRes.json();
        countryVal = geoData.country_name || "Malaysia";
        stateVal = geoData.region || "";
      }
    } catch (err) {
      console.warn("CRM location lookup failed (offline/blocked):", err);
    }

    const crmPayload = {
      companyName: companyVal || "Unknown Company",
      contactName: nameVal,
      phone: phoneVal,
      email: emailVal,
      country: countryVal,
      regionState: stateVal,
      source: "Website Form",
      dealValue: 5000,
      initialNote: `Enquiry: ${messageVal}`
    };

    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const webhookUrl = isLocalhost
      ? "http://localhost:5000/api/webhooks/leads"
      : "https://api.crm.ascentbw.com/api/webhooks/leads";

    try {
      fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(crmPayload)
      }).catch(err => console.warn("CRM lead intake failed (offline):", err));
    } catch (err) {
      console.warn("CRM lead intake failed (offline):", err);
    }

    try {
      const response = await fetch(
        "https://formsubmit.co/ajax/admin@ascentbw.com",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        }
      );

      if (response.ok) {
        alert("Thank you! Your inquiry has been submitted.");
        formRef.current.reset();
        onClose(); // optional: close drawer after submit
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (error) {
      alert("Network error. Please try again later.");
    }
  };

  /* ---------------- SCROLL EFFECTS ---------------- */
  const { scrollYProgress } = useScroll({
    container: containerRef,
    offset: ["start start", "end end"],
  });

  const paddingX = useTransform(scrollYProgress, [0, 0.2], ["5%", "0%"]);
  const marginTop = useTransform(scrollYProgress, [0, 0.2], ["125px", "0px"]);
  const borderRadius = useTransform(scrollYProgress, [0, 0.2], ["30px", "0px"]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [0.94, 1]);

  /* ---------------- LOCK BACKGROUND SCROLL ---------------- */
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  return (
    <>
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 35, stiffness: 250 }}
        ref={containerRef}
        data-lenis-prevent
        className="fixed inset-0 z-[9999] overflow-y-auto bg-black/60 backdrop-blur-md hide-scrollbar"
      >
        {/* EXPANDING CARD */}
        <motion.div
          style={{
            paddingLeft: paddingX,
            paddingRight: paddingX,
            marginTop,
            borderRadius,
            scale,
            backgroundColor: "white",
            /* minHeight: "150vh", */
            transformOrigin: "top center",
          }}
          className="relative shadow-2xl"
        >
          {/* CLOSE BUTTON */}
          <div className="sticky top-0 z-[10001] flex justify-end p-6 pointer-events-none">
            <button
              onClick={onClose}
              className="bg-[#001233] text-white p-3 rounded-full pointer-events-auto hover:scale-110 transition-all shadow-lg"
              aria-label="Close Contact Form"
            >
              <IoClose size={20} />
            </button>
          </div>

          <Container className="py-10">
            <Row className="mb-16 align-items-center">
              <Col lg={7}>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-5xl md:text-7xl font-bold mb-3 text-[#001233] leading-[0.85]"
                >
                  Let's Ascent <br /> Together
                </motion.h1>
              </Col>

              <Col lg={5}>
                <motion.p
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-lg md:text-xl text-gray-500 max-w-sm"
                >
                  Share a few details about your project and we'll get back to you shortly.
                </motion.p>
              </Col>
            </Row>

            {/* ---------------- FORM ---------------- */}
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="space-y-12 pb-32"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
                {/* NAME */}
                <div className="flex flex-col border-b pb-4">
                  <label className="text-sm font-bold text-[#001233] uppercase opacity-60">
                    Your Name*
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="text-2xl bg-transparent outline-none"
                  />
                </div>

                {/* EMAIL */}
                <div className="flex flex-col border-b pb-4">
                  <label className="text-sm font-bold text-[#001233] uppercase opacity-60">
                    Email Address*
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Email address"
                    className="text-2xl bg-transparent outline-none"
                  />
                </div>

                {/* COMPANY */}
                <div className="flex flex-col border-b pb-4">
                  <label className="text-sm font-bold text-[#001233] uppercase opacity-60">
                    Company / Brand*
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="Company name"
                    className="text-2xl bg-transparent outline-none"
                  />
                </div>

                {/* PHONE */}
                <div className="flex flex-col border-b pb-4">
                  <label className="text-sm font-bold text-[#001233] uppercase opacity-60">
                    Phone Number / WhatsApp*
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="Phone number with country code"
                    className="text-2xl bg-transparent outline-none"
                  />
                </div>

                {/* MESSAGE */}
                <div className="md:col-span-2">
                  <label className="text-sm font-bold text-[#001233] uppercase opacity-60">
                    Enquiry*
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    required
                    className="w-full border-2 rounded-2xl p-6 text-xl outline-none"
                  />
                </div>
              </div>

              {/* Hidden FormSubmit config */}
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_template" value="table" />
              <input
                type="hidden"
                name="_subject"
                value="Ascent Website Contact Form"
              />

              <button
                type="submit"
                className="
    px-10 py-4 text-lg
    sm:px-12 sm:py-5 sm:text-lg
    md:px-16 md:py-6 md:text-xl
    bg-[#002250] text-white
    rounded-full font-bold shadow-xl
    hover:scale-105 transition-all
  "
              >
                Submit Inquiry
              </button>

            </form>
          </Container>
        </motion.div>
      </motion.div>
    </>
  );
};

export default ContactStack;
